"use client"

import * as React from "react"
import { Check, ChevronsUpDown, Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

// CreatableSelect - Searchable dropdown that allows creating new options
export function CreatableSelect({ 
  options = [], 
  value, 
  onValueChange,
  onChange,
  onCreateOption,
  placeholder = "Select or create...",
  searchPlaceholder = "Search or type to add...",
  emptyMessage = "No results found.",
  createLabel = "Add",
  className,
  disabled = false,
  canCreate = true,
  testId
}) {
  // Backwards-compat: some callers historically passed `onChange` instead of `onValueChange`.
  const handleChange = onValueChange || onChange || (() => {})
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const filteredOptions = React.useMemo(() => {
    if (!search) return options
    return options.filter(option => 
      option.toLowerCase().includes(search.toLowerCase())
    )
  }, [options, search])

  // Check if the current search value matches any existing option (case-insensitive)
  const exactMatch = React.useMemo(() => {
    return options.some(option => option.toLowerCase() === search.toLowerCase())
  }, [options, search])

  const handleCreate = () => {
    if (search.trim() && !exactMatch && canCreate) {
      // Capitalize first letter of each word for consistency
      const formattedValue = search.trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      
      if (onCreateOption) {
        onCreateOption(formattedValue)
      }
      handleChange(formattedValue)
      setOpen(false)
      setSearch("")
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && search.trim() && !exactMatch && canCreate) {
      e.preventDefault()
      handleCreate()
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          id={testId}
          data-testid={testId}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start" data-testid={testId ? `${testId}-menu` : undefined}>
        <Command shouldFilter={false}>
          <CommandInput 
            data-testid={testId ? `${testId}-search` : undefined}
            placeholder={searchPlaceholder} 
            value={search}
            onValueChange={setSearch}
            onKeyDown={handleKeyDown}
          />
          <CommandList>
            {filteredOptions.length === 0 && !search.trim() && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
            
            {/* Show "Add new" option when search doesn't match existing */}
            {search.trim() && !exactMatch && canCreate && (
              <CommandGroup>
                <CommandItem
                  onSelect={handleCreate}
                  data-testid={testId ? `${testId}-create` : undefined}
                  className="text-primary cursor-pointer"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createLabel} "{search.trim()}"
                </CommandItem>
              </CommandGroup>
            )}
            
            {filteredOptions.length > 0 && (
              <CommandGroup className="max-h-60 overflow-auto">
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option}
                    data-testid={testId ? `${testId}-option-${option.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}` : undefined}
                    // Force-unique value so cmdk's case-insensitive matching never
                    // re-fires the originally-selected item by mistake.
                    value={`opt:${option}`}
                    onSelect={() => {
                      handleChange(option)
                      setOpen(false)
                      setSearch("")
                    }}
                    onMouseDown={(e) => {
                      // Belt-and-suspenders: ensure the click registers even if
                      // cmdk's keyboard-nav state is stale.
                      e.preventDefault()
                      handleChange(option)
                      setOpen(false)
                      setSearch("")
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            
            {filteredOptions.length === 0 && search.trim() && exactMatch && (
              <CommandEmpty>Type to search programs</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default CreatableSelect
